import React, { useState, useRef, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import deepEqual from 'deep-equal';
import { v4 as uuidv4 } from 'uuid';

import { ReactSVGPanZoom, INITIAL_VALUE, TOOL_NONE, pan, openMiniature, closeMiniature } from 'react-svg-pan-zoom';
import Toolbar from './svgPanZoomToolbar';

const CustomSvgPanZoom = (props) => {
	const {
		reference,
		content,
		width,
		height,
		onClick = () => {},
		onDoubleClick,
		defaultTool = TOOL_NONE,
		CustomToolbarItems,
		svgWidth = width,
		svgHeight = height,
		loadSVG,
		activeState,
		processViewLink,
		toolbarProps = {},
		compClass = '',
	} = props;

	const [tool, setTool] = useState(defaultTool);
	const [value, setValue] = useState(INITIAL_VALUE); // Lock the viewer to a specific value
	const [id] = useState(uuidv4());
	const Viewer = useRef();

	const changeTool = (newTool) => {
		setTool((old) => (deepEqual(newTool, old) ? old : newTool));
	};
	const changeValue = (newValue) => {
		setValue((old) => (deepEqual(newValue, old) ? old : newValue));
	};

	// own pan implementation on default tool
	const activeMouseEvent = useRef(null);
	const lastMouseEvent = useRef(null);
	useEffect(() => {
		const onMouseMove = (event) => {
			if (activeMouseEvent.current) {
				const newX = event.clientX;
				const newY = event.clientY;
				const deltaX = activeMouseEvent.current.currentX - newX;
				const deltaY = activeMouseEvent.current.currentY - newY;
				setValue((old) => {
					const zoomLevel = 1 / old.a; // this is not actually the zoomlevel, but its close enough for us
					return pan(old, -deltaX * zoomLevel, -deltaY * zoomLevel);
				});
				activeMouseEvent.current.currentX = newX;
				activeMouseEvent.current.currentY = newY;
			}
		};
		const onMouseDown = (event) => {
			// we dont want to interfer with other tools -> custom logic only works with default tool
			if (tool === TOOL_NONE) {
				activeMouseEvent.current = {
					startX: event.clientX,
					currentX: event.clientX,
					startY: event.clientY,
					currentY: event.clientY,
				};
			}
		};
		const onMouseUp = (event) => {
			if (activeMouseEvent.current) {
				const endX = event.clientX;
				const endY = event.clientY;
				const deltaX = activeMouseEvent.current.startX - endX;
				const deltaY = activeMouseEvent.current.startY - endY;

				// handle click if the event wasnt a move event
				if (deltaX === 0 && deltaY === 0) {
					// wait for 250ms for another mouseup event to arive
					//  if yes: call onDoubleClick; if no: call onClick
					if (lastMouseEvent.current) {
						clearTimeout(lastMouseEvent.current);
						lastMouseEvent.current = null;
						onDoubleClick(event);
					} else {
						lastMouseEvent.current = setTimeout(() => {
							onClick(event);
							lastMouseEvent.current = null;
						}, 250);
					}
				}

				activeMouseEvent.current = null;
			}
		};

		const divWrapper = document.getElementById(id);

		divWrapper.addEventListener('mousedown', onMouseDown);
		divWrapper.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', onMouseUp);

		return () => {
			divWrapper.removeEventListener('mousedown', onMouseDown);
			divWrapper.removeEventListener('mousemove', onMouseMove);
			document.removeEventListener('mouseup', onMouseUp);
		};
	}, [id, tool, onClick, onDoubleClick]);

	const customToolbar = () => (
		<Toolbar
			tool={tool}
			onChangeTool={changeTool}
			value={value}
			onChangeValue={changeValue}
			SVGAlignX='center'
			SVGAlignY='center'
			loadSVG={loadSVG}
			parentGUID={activeState.parentGUID}
			activeGUID={activeState.guid}
			processViewLink={processViewLink}
			ToolbarItems={CustomToolbarItems}
		/>
	);

	const customViewerRef = useMemo(
		/* 
			I literally cannot explain you why I did this. Sorry.
			The problem was that bc of reasons the Viewer reference got null when you opened the widget in fullscreen. and it did not fix afterwards.
			I thought one solution might be to do this here but with func: (args) => setValue(old => func(old, args)). This did not work at all. I have no clue why. 
			So I just tried what you can now see down below and it worked. Whatever.
		*/
		() => ({
			setPointOnViewerCenter: (SVGPointX, SVGPointY, scaleFactor) => {
				Viewer.current?.setPointOnViewerCenter(SVGPointX, SVGPointY, scaleFactor);
			},
			reset: () => {
				Viewer.current?.reset();
			},
			pan: (SVGDeltaX, SVGDeltaY) => {
				Viewer.current?.pan(SVGDeltaX, SVGDeltaY);
			},
			zoom: (SVGPointX, SVGPointY, scaleFactor) => {
				Viewer.current?.zoom(SVGPointX, SVGPointY, scaleFactor);
			},
			fitSelection: (selectionSVGPointX, selectionSVGPointY, selectionWidth, selectionHeight) => {
				Viewer.current?.fitSelection(selectionSVGPointX, selectionSVGPointY, selectionWidth, selectionHeight);
			},
			fitToViewer: (SVGAlignX = 'left', SVGAlignY = 'top') => {
				Viewer.current?.fitToViewer(SVGAlignX, SVGAlignY);
			},
			zoomOnViewerCenter: (scaleFactor) => {
				Viewer.current?.zoomOnViewerCenter(scaleFactor);
			},
			openMiniature: () => {
				openMiniature();
			},
			closeMiniature: () => {
				closeMiniature();
			},
		}),
		[]
	);
	reference.current = customViewerRef;

	return (
		<div id={id}>
			<ReactSVGPanZoom
				ref={Viewer}
				width={width}
				height={height}
				tool={tool}
				onChangeTool={changeTool}
				value={value}
				onChangeValue={changeValue}
				toolbarProps={toolbarProps}
				customToolbar={customToolbar}
				className={compClass}
				onClick={(event) => {
					// we implement our own onClick in the custom pan logic so we dont want this here to interfer
					event.preventDefault();
				}}
			>
				<svg width={svgWidth} height={svgHeight}>
					{content}
				</svg>
			</ReactSVGPanZoom>
		</div>
	);
};

const mapStateToProps = (state) => ({
	activeState: state.activeState.state,
});

export default connect(mapStateToProps)(CustomSvgPanZoom);
